import { Alert, Button, Divider } from '@design-system';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import {
  PaymentMethodCreateParams,
  SetupIntentResult,
  StripeError,
} from '@stripe/stripe-js';
import { useConvertCurrency } from '@utils/currencyConverter';
import { framedByDefault } from '@utils/frames';
import { normaliseShopifyPrice } from '@utils/prices';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import FrameSelector from '../../../../components/Containers/ReviewCheckoutDrawer/FrameSelector';
import { CustomsCode } from '../../../../components/Containers/ReviewCheckoutDrawer/PaymentMethod/CustomsCode';
import { ReviewItem } from '../../../../components/Containers/ReviewCheckoutDrawer/ReviewItem';
import { AML_LIMIT } from '../../../../constants/product';
import { useDialogContext } from '../../../../hooks/useDialogContext';
import { useIsPreOrder } from '../../../../hooks/useIsPreOrder';
import { useAuth, useFlow } from '../../../../providers';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import { usePurchase } from '../../../../providers/PurchaseProvider';
import { useStore } from '../../../../store/utils';
import {
  GeneratedDrawType,
  GeneratedOrderPricingQuote,
} from '../../../../types/generated';
import { ProductV2Storyblok } from '../../../../types/generated-storyblok';
import { CurrencyCode } from '../../../../types/Locations';
import { TrackGtagEvent } from '../../../../utils/analytics';
import { useAnalyticsEmitter } from '../../../../utils/analytics/emitter';
import { AnalyticsFlowProvider } from '../../../../utils/analytics/flow';
import {
  captureBreadcrumb,
  captureError,
  captureErrorMessage,
} from '../../../../utils/capture';
import { drawEntryActions } from '../../store/draw-entry';
import { PaymentType } from '../../types';
import { getBackgroundImage } from '../../utils/images';
import { DrawEntryConfirmationDialog } from '../confirmation/DrawEntryConfirmationDialog';
import { PaymentMethodButton } from '../payment-methods/PaymentMethodButton';
import { ShippingAddressButton } from '../shipping-address/ShippingAddressButton';
import { ReviewDrawFooter } from './ReviewDrawFooter';
import { ReviewPricingInformation } from './ReviewPricingInformation';

type Props = {
  product: ProductType;
};

export const ReviewDrawer: React.FC<Props> = ({ product }) => {
  const drawController = usePreAuthDrawCtx();
  const isPreOrder = useIsPreOrder(drawController.draw);
  const purchasingController = usePurchase();
  const { closeFlow, goToStep } = useFlow();
  const { hideDialog, showDialog } = useDialogContext();
  const dispatch = useDispatch();
  const { t } = useTranslation(['product', 'countries']);
  const [working, setWorking] = useState(false);
  const { user } = useAuth();
  const elements = useElements();
  const stripe = useStripe();
  const [emitter] = useAnalyticsEmitter();

  const isFramedByDefault = framedByDefault(
    purchasingController.product?.framingOffered,
  );

  const drawEntry = useStore((s) =>
    drawController.draw
      ? s.features.draw.drawEntry[drawController.draw.id]
      : { entering: false, entered: false },
  );

  const drawSupportsFrame =
    purchasingController.frameOptions &&
    purchasingController.frameOptions.length > 0;

  const {
    entryIntentResponse,
    loading,
    selectedPaymentMethodId,
    selectedPaymentType,
    updating,
    usingNewPaymentMethod,
  } = drawController;

  const payWithCrypto = selectedPaymentType === PaymentType.CRYPTO;
  const paymentReady = Boolean(
    payWithCrypto || selectedPaymentMethodId || usingNewPaymentMethod,
  );
  const currencyConverter = useConvertCurrency();
  const workingOrEntering = working || Boolean(drawEntry?.entering);
  const disableEditing = workingOrEntering;
  const drawStale = loading || updating;
  const drawDataReady = entryIntentResponse?.readyToConfirm && paymentReady;
  const readyToConfirmDraw = drawDataReady && !drawStale;

  const eventProperties = useMemo(
    () => ({
      'type': drawController.draw?.drawType,
      'artist_id': product.productArtist?.map(
        (a: ResolvedRelatedArtist) => a.artist.internalId,
      )?.[0],
      ...(isPreOrder
        ? {
            'preorder_application_count': 1,
            'preorder_intent_id':
              drawController.entryIntentResponse?.intent?.id,
            'public_preorder_id': drawController.draw?.id,
          }
        : {
            'draw_entry_count': 1,
            'draw_intent_id': drawController.entryIntentResponse?.intent?.id,
            'public_draw_id': drawController.draw?.id,
          }),
      'product_id': product.id[0].id,
      'product_name': product.title,
      'user_email': user?.email,
      'user_id': user?.id,
    }),
    [drawController, isPreOrder, product, user],
  );

  useEffect(() => {
    if (drawEntry?.entered) {
      closeFlow();

      let isAmlRequired = false;
      if (drawController.entryIntentResponse.pricing?.pricing) {
        const total = normaliseShopifyPrice(
          drawController.entryIntentResponse.pricing?.pricing?.totalPrice,
          drawController.entryIntentResponse.pricing.pricing
            .currencyCode as CurrencyCode,
        );

        isAmlRequired = Boolean(
          total >
            currencyConverter.convert(
              AML_LIMIT,
              drawController.entryIntentResponse.pricing.pricing.currencyCode,
            ),
        );
      }

      showDialog(
        <AnalyticsFlowProvider
          name={isPreOrder ? 'pre-order success' : 'draw success'}
          properties={{
            'payment method': drawController.selectedPaymentType,
            'type': drawController.draw?.drawType,
          }}
        >
          {() => (
            <DrawEntryConfirmationDialog
              backgroundImage={getBackgroundImage(product)}
              bid={drawController.entryIntentResponse.intent?.bid}
              isAmlRequired={isAmlRequired}
              isPreOrder={isPreOrder}
              onClose={hideDialog}
              productTitle={product.title}
            />
          )}
        </AnalyticsFlowProvider>,
      );

      emitter(isPreOrder ? 'application' : 'entry', 'success', eventProperties);

      TrackGtagEvent(
        isPreOrder ? 'preorder_application' : 'draw_entry',
        eventProperties,
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawEntry?.entered]);

  function handleStripeResponse(
    response: SetupIntentResult | { error: StripeError },
  ) {
    if (
      drawController.draw &&
      drawController.entryIntentResponse?.intent &&
      !response.error
    ) {
      drawController.setPaymentErrorCode(null);

      // Handle successful setup intent
      dispatch(
        drawEntryActions.setupDrawEntry({
          actionId: drawController.draw.id,
          drawEntryIntentId: drawController.entryIntentResponse.intent.id,
          polling: true,
          freshEntry: true,
          entering: true,
          entered: false,
        }),
      );

      return;
    }

    const { error } = response;
    if (error) {
      drawController.setPaymentErrorCode(error.code || error.type);
      console.error(error);
    }
  }

  async function handleDrawEntry() {
    captureBreadcrumb({
      level: 'info',
      message: `${user?.id} has clicked "Enter Draw"`,
      category: 'draw',
    });

    if (!stripe || !elements) {
      captureErrorMessage('Stripe SDK is not setup');

      return;
    }

    emitter('submit', 'clicked');
    drawController.setPaymentErrorCode(null);
    if (payWithCrypto) {
      goToStep('crypto_com');
      return;
    }

    setWorking(true);
    try {
      const url = new URL(window.location.href);

      if (drawController.entryIntentResponse?.intent) {
        url.searchParams.set(
          'dei',
          drawController.entryIntentResponse.intent.id,
        );
      }

      // This means we're using an existing payment method, so we just need to pass in
      // the setup secret returned from the backend to confirm the setup intent using
      // that payment method.
      if (drawController.selectedPaymentMethodId) {
        const response = await stripe.confirmSetup({
          clientSecret: drawController.setupSecret || '',
          confirmParams: {
            return_url: url.toString(),
          },
          redirect: 'if_required',
        });

        handleStripeResponse(response);

        return;
      }

      // This means we're creating a payment method with the SCA action. So instead of
      // passing in the client secret we've setup on the payable, we want to pass in
      // the payment elements SDK. This is used to fetch the tokenized card data, and then
      // confirm the setup intent using that data.
      // eslint-disable-next-line prettier/prettier
      let billingDetails: PaymentMethodCreateParams.BillingDetails | undefined =
        void 0;

      // If the follow flag is set to `true` then we need to copy the shipping address
      // across to the setup intent confirmation options. Otherwise the Stripe Elements
      // SDK will pull through the billing address that has been entered on the "Add
      // Payment Element" with the Stripe Address Element.
      if (drawController.newPaymentMethodUsingShippingAddress) {
        const selectedAddress = drawController.addresses.find(
          (a) => a.id === drawController.selectedAddress,
        );

        if (!selectedAddress) throw new Error('No shipping address selected');

        billingDetails = {
          // eslint-disable-next-line prettier/prettier
          name: [selectedAddress.firstName, selectedAddress.lastName].join(' '),
          phone: selectedAddress.phone ?? void 0,
          email: user?.email ?? '',
          address: {
            // None of these fields will be undefined, we should fix the types for this!
            line1: selectedAddress.addressLine1 ?? '',
            line2: selectedAddress.addressLine2 ?? '',
            city: selectedAddress.city ?? '',
            country: selectedAddress.countryCode ?? '',
            postal_code: selectedAddress.zip ?? '',
            state: selectedAddress.provinceName ?? '',
          },
        };
      } else {
        const storedBillingAddress = drawController.billingAddress;

        billingDetails = {
          name: storedBillingAddress?.name ?? '',
          phone: storedBillingAddress?.phone ?? '',
          email: user?.email ?? '',
          address: {
            line1: storedBillingAddress?.address?.line1 ?? '',
            line2: storedBillingAddress?.address?.line2 ?? '',
            city: storedBillingAddress?.address?.city ?? '',
            country: storedBillingAddress?.address?.country ?? '',
            postal_code: storedBillingAddress?.address?.postal_code ?? '',
            state: storedBillingAddress?.address?.state ?? '',
          },
        };
      }

      const response = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: url.toString(),
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
        redirect: 'if_required',
      });

      handleStripeResponse(response);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="flex flex-col space-y-6">
      {drawController.errorMessage && (
        <div className="w-full flex-1">
          <Alert status="error">
            {typeof drawController.errorMessage === 'boolean'
              ? t('product:drawers.review.draw.failureMessageGeneric')
              : drawController.errorMessage}
          </Alert>
        </div>
      )}

      <div className="flex w-full flex-col space-y-4">
        <div
          className={classNames(
            'flex flex-col space-y-4 rounded-sm border border-divider/20 p-4',
            { 'cursor-not-allowed bg-neutral-1': disableEditing },
          )}
        >
          <ReviewItem
            allowMultiples={
              purchasingController.product?.allowMultipleProducts ?? false
            }
            allowMultiplesLimit={
              purchasingController.product?.maximumSalesCapacity ?? null
            }
            artist={purchasingController.product?.artist ?? ''}
            disabled={disableEditing}
            imageFocus={
              product.component === 'product_v2'
                ? (product as unknown as ProductV2Storyblok).ogImage?.focus ??
                  ''
                : product.thumbnail?.focus ?? ''
            }
            imageUrl={
              product.component === 'product_v2'
                ? (product as unknown as ProductV2Storyblok).ogImage.filename
                : product.thumbnail.filename
            }
            price={
              drawController.draw?.drawType === GeneratedDrawType.Auction
                ? undefined
                : purchasingController.product?.price
            }
            quantity={purchasingController.quantity}
            title={purchasingController.product?.name ?? ''}
            updateQuantity={(value) => {
              if (value < purchasingController.quantity)
                purchasingController.setQuantity(value);
            }}
          />
          {drawSupportsFrame && !isFramedByDefault ? (
            <>
              <Divider />
              <FrameSelector
                currencyCode={
                  drawController.draw?.auctionReservePrice
                    ?.currencyCode as CurrencyCode
                }
                disabled={disableEditing}
                product={product}
                purchasingController={purchasingController}
                setVisibleForm={(formType) => {
                  if (formType === 'frame') {
                    emitter('framing', 'clicked', {
                      'product frame chosen':
                        drawController.optionalItems.length > 0,
                    });
                  }
                  switch (formType) {
                    case 'locale':
                      goToStep('locale');
                      break;
                    case 'frame':
                      goToStep('frame');
                      break;
                    default:
                      goToStep('apply');
                  }
                }}
              />
            </>
          ) : null}
        </div>

        <div className="flex flex-col space-y-2">
          <div className="text-xs">
            {t('product:drawers.review.draw.shipTo')}
          </div>
          <ShippingAddressButton
            disabled={disableEditing}
            drawController={drawController}
          />
        </div>

        <CustomsCode
          address={drawController.addresses.find(
            (a) => a.id === drawController.selectedAddress,
          )}
          onClick={() => {
            goToStep('edit_shipping_address');
          }}
        />

        <div className="flex flex-col space-y-2">
          <div className="text-xs">
            {t('product:drawers.review.draw.payWith')}
          </div>
          <PaymentMethodButton
            disabled={disableEditing}
            isPreOrder={isPreOrder}
          />
        </div>

        {drawController.entryIntentResponse.pricing && (
          <ReviewPricingInformation
            bid={drawController.entryIntentResponse.intent?.bid}
            isPreOrder={isPreOrder}
            paymentReady={paymentReady}
            payWithCrypto={payWithCrypto}
            pricingBreakdown={
              drawController.entryIntentResponse
                .pricing as GeneratedOrderPricingQuote
            }
            updating={drawController.updating}
          />
        )}

        <div className="!mt-6 flex flex-col space-y-3">
          <Button
            disabled={!readyToConfirmDraw}
            fullWidth
            label={
              isPreOrder
                ? t('product:drawers.review.preOrderButton')
                : t('product:drawers.review.draw.enterButton')
            }
            loading={workingOrEntering}
            onClick={() => {
              handleDrawEntry().catch((error) => {
                console.error(error);
                captureError(error as Error);
              });
            }}
            size="lg"
            variant="primary"
          />
        </div>
        {readyToConfirmDraw && (
          <ReviewDrawFooter
            payWithCrypto={payWithCrypto}
            productTags={purchasingController.product?.tags}
            productTitle={product.title}
          />
        )}
      </div>
    </div>
  );
};
